/**
 * DropdownMenu: an action menu attached to a trigger.
 *
 *   <DropdownMenu
 *     label="Actions for this run"                      accessible name when the trigger is icon-only
 *     trigger={<Icon icon="mdi:dots-vertical" />}      any node; it is wrapped in a real <button>
 *     items={[
 *       { label: "Open in Scrape", icon: "mdi:open-in-new", href: "/dashboard/playground?log=1" },
 *       { label: "Copy ID", icon: "mdi:content-copy", onClick: copyId },
 *       { divider: true },
 *       { label: "Delete", icon: "mdi:delete-outline", color: "text-danger-text", onClick: remove },
 *     ]}
 *     position="bottom-right"
 *   />
 *
 * Keyboard: Enter, Space or ArrowDown on the trigger opens and focuses the first item; arrows, Home and End move;
 * Escape closes and returns focus to the trigger; Tab closes. The trigger carries aria-haspopup and aria-expanded.
 * If `trigger` is itself a <button> element or an IconButton it is used as the trigger directly (no nested buttons).
 * Clicks on the trigger and on items never bubble to a parent row, so menus inside clickable table rows are safe.
 * The menu is portaled, positioned with `position: fixed`, flipped and clamped so it stays inside the viewport.
 *
 * position     bottom-left (default) | bottom-right | top-left | top-right   (preferred side; flips if no room)
 * size         default | small
 * matchTriggerWidth  menu is exactly as wide as the trigger (select-style pickers)
 * Item fields: label, icon (Iconify name or a node), description, indicator, onClick, href, external,
 *              color (text class), badge, badgeColor, disabled
 *
 * Rich items (like a "Create" menu): give items a `description` and they render as icon + title + one grey line
 * below it. The menu widens to fit (up to 340px) and descriptions wrap instead of truncating.
 *   { label: "Batch scrape", icon: "lucide:layers", description: "Up to 50 URLs in one job.", href: "/dashboard/batch" }
 * `indicator: true` shows a small unread dot after the label (used by Help > What's new).
 */
import Link from "next/link";
import { Icon } from "@iconify/react";
import {
  cloneElement,
  isValidElement,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useEscape, useLayer } from "@/hooks/useOverlay";
import IconButton from "./IconButton";

export interface DropdownItem {
  label: string;
  /** Iconify name (e.g. "lucide:key-round") or a node. */
  icon?: string | ReactNode;
  /** One short grey line under the label. */
  description?: string;
  /** Small unread dot after the label. */
  indicator?: boolean;
  color?: string; // text color class
  badge?: string;
  badgeColor?: string;
  onClick?: () => void;
  href?: string;
  external?: boolean; // opens href in a new tab instead of Next's Link routing
  disabled?: boolean;
}

// A divider entry, e.g. { divider: true }, to group items visually.
export interface DropdownDivider {
  divider: true;
}

export type DropdownEntry = DropdownItem | DropdownDivider;

function isDivider(entry: DropdownEntry): entry is DropdownDivider {
  return "divider" in entry && entry.divider === true;
}

interface DropdownMenuProps {
  trigger: ReactNode;
  items: DropdownEntry[];
  position?: "bottom-left" | "bottom-right" | "top-left" | "top-right";
  className?: string;
  size?: "default" | "small";
  matchTriggerWidth?: boolean;
  /** Accessible name for the trigger. Required when the trigger has no visible text. */
  label?: string;
  /** Class for the trigger button (only when the trigger is not already a <button>). */
  triggerClassName?: string;
  /** Extra props for aria wiring from a form label, e.g. { "aria-labelledby": id }. */
  triggerProps?: Record<string, string | undefined>;
}

const GAP = 6;
const EDGE = 8;

const DropdownMenu = ({
  trigger,
  items,
  position = "bottom-left",
  className = "",
  size = "default",
  matchTriggerWidth = false,
  label,
  triggerClassName = "",
  triggerProps = {},
}: DropdownMenuProps) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties>({ position: "fixed", visibility: "hidden" });
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const focusOnOpen = useRef<"first" | "last" | null>(null);
  const menuId = useId();
  const isTop = useLayer(open);
  const rich = items.some(e => !isDivider(e) && !!e.description);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
  }, []);

  useEscape(open, () => close(true), isTop);

  // Outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Position: preferred side, flipped when there is no room, clamped to the viewport.
  const place = useCallback(() => {
    const t = triggerRef.current;
    const m = menuRef.current;
    if (!t || !m) return;
    const r = t.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const minWidth = matchTriggerWidth ? r.width : rich ? 280 : size === "small" ? 160 : 200;
    const mw = Math.max(m.offsetWidth, minWidth);
    const mh = m.offsetHeight;
    const wantTop = position.startsWith("top");
    const alignRight = position.endsWith("right");
    const spaceBelow = vh - r.bottom - GAP - EDGE;
    const spaceAbove = r.top - GAP - EDGE;
    const placeTop = wantTop ? spaceAbove >= mh || spaceAbove > spaceBelow : spaceBelow < mh && spaceAbove > spaceBelow;
    let top = placeTop ? r.top - GAP - mh : r.bottom + GAP;
    top = Math.max(EDGE, Math.min(top, vh - mh - EDGE));
    let left = alignRight ? r.right - mw : r.left;
    left = Math.max(EDGE, Math.min(left, vw - mw - EDGE));
    setStyle({
      position: "fixed",
      top,
      left,
      zIndex: 10000,
      minWidth,
      width: matchTriggerWidth ? r.width : undefined,
      maxWidth: vw - EDGE * 2,
      maxHeight: Math.max(160, placeTop ? spaceAbove : spaceBelow),
      visibility: "visible",
    });
  }, [matchTriggerWidth, position, size, rich]);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(s => ({ ...s, visibility: "hidden" }));
      return;
    }
    place();
    const want = focusOnOpen.current;
    focusOnOpen.current = null;
    if (!want) return;
    // Wait for the positioned (visible) menu before moving focus into it.
    const raf = requestAnimationFrame(() => {
      const items = itemRefs.current.filter(Boolean) as HTMLElement[];
      (want === "first" ? items[0] : items[items.length - 1])?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(raf);
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open, place]);

  const toggle = (e: ReactMouseEvent) => {
    e.stopPropagation();
    // Keyboard "clicks" (Enter/Space) arrive with detail 0; move focus into the menu for those.
    focusOnOpen.current = e.detail === 0 ? "first" : null;
    setOpen(o => !o);
  };

  const onTriggerKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      focusOnOpen.current = e.key === "ArrowDown" ? "first" : "last";
      setOpen(true);
    }
  };

  const onMenuKeyDown = (e: ReactKeyboardEvent) => {
    const list = itemRefs.current.filter(Boolean) as HTMLElement[];
    const i = list.indexOf(document.activeElement as HTMLElement);
    const go = (n: number) => list[(n + list.length) % list.length]?.focus();
    const target =
      e.key === "ArrowDown" ? i + 1 : e.key === "ArrowUp" ? (i < 0 ? list.length - 1 : i - 1) : e.key === "Home" ? 0 : e.key === "End" ? list.length - 1 : null;
    if (target !== null) {
      e.preventDefault();
      go(target);
    } else if (e.key === "Tab") {
      close(false);
    }
  };

  const triggerA11y = {
    "aria-haspopup": "menu" as const,
    "aria-expanded": open,
    "aria-controls": open ? menuId : undefined,
    ...(label ? { "aria-label": label } : {}),
    ...triggerProps,
  };

  let triggerNode: ReactNode;
  if (isValidElement(trigger) && (trigger.type === "button" || trigger.type === IconButton)) {
    const el = trigger as ReactElement<React.ButtonHTMLAttributes<HTMLButtonElement> & { ref?: unknown }>;
    triggerNode = cloneElement(el, {
      ...triggerA11y,
      type: el.props.type ?? "button",
      ref: (node: HTMLButtonElement | null) => {
        triggerRef.current = node;
        // Keep the caller's own ref on the button working (React 19 passes ref as a prop).
        const own = el.props.ref as React.Ref<HTMLButtonElement> | undefined;
        if (typeof own === "function") own(node);
        else if (own && typeof own === "object") (own as React.RefObject<HTMLButtonElement | null>).current = node;
      },
      onClick: (e: ReactMouseEvent<HTMLButtonElement>) => {
        el.props.onClick?.(e);
        toggle(e);
      },
      onKeyDown: (e: ReactKeyboardEvent<HTMLButtonElement>) => {
        el.props.onKeyDown?.(e);
        onTriggerKeyDown(e);
      },
    } as Record<string, unknown>);
  } else {
    triggerNode = (
      <button
        type="button"
        ref={node => {
          triggerRef.current = node;
        }}
        {...triggerA11y}
        onClick={toggle}
        onKeyDown={onTriggerKeyDown}
        className={`block w-full cursor-pointer rounded-control text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${triggerClassName}`}
      >
        {trigger}
      </button>
    );
  }

  let itemIndex = -1;
  itemRefs.current = [];
  const menu = (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={label}
          style={style}
          onKeyDown={onMenuKeyDown}
          onClick={e => e.stopPropagation()}
          className={`flex flex-col gap-0.5 overflow-y-auto rounded-card border border-line bg-raised shadow-popover ${
            size === "small" ? "p-1 min-w-[160px]" : "p-1.5 min-w-[200px]"
          } ${rich ? "w-[340px]" : ""} ${className}`}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.12, ease: "easeOut" }}
        >
          {items.map((entry, idx) => {
            if (isDivider(entry)) {
              return <div key={`divider-${idx}`} role="separator" className="my-1 border-t border-line" />;
            }
            const item = entry;
            itemIndex++;
            const myIndex = itemIndex;
            const hasDesc = !!item.description;
            const cls = `flex w-full ${hasDesc ? "items-start" : "items-center"} justify-between gap-3 rounded-md text-left outline-none transition-colors duration-100 cursor-pointer hover:bg-black/5 focus-visible:bg-black/5 focus-visible:outline-none dark:hover:bg-white/10 dark:focus-visible:bg-white/10 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 ${
              item.color ?? "text-fg-secondary"
            } ${size === "small" ? "px-2.5 py-1.5 text-xs" : hasDesc ? "px-2.5 py-2 text-sm" : "px-3 py-2 text-sm"}`;
            const iconNode = !item.icon ? null : typeof item.icon === "string" ? (
              <Icon
                icon={item.icon}
                className={`shrink-0 ${size === "small" ? "text-base" : hasDesc ? "mt-0.5 text-base" : "text-lg"} ${item.color ?? "text-fg-muted"}`}
                aria-hidden="true"
              />
            ) : (
              <span className={`flex shrink-0 ${hasDesc ? "mt-0.5" : ""} ${item.color ?? "text-fg-muted"}`} aria-hidden="true">
                {item.icon}
              </span>
            );
            const dot = item.indicator && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />;
            const inner = (
              <>
                <span className={`flex min-w-0 ${hasDesc ? "items-start gap-2.5" : `items-center ${size === "small" ? "gap-2" : "gap-2.5"}`}`}>
                  {iconNode}
                  {hasDesc ? (
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="flex items-center gap-1.5 font-medium text-fg">
                        {item.label}
                        {dot}
                      </span>
                      <span className="text-13 font-normal leading-snug text-fg-muted">{item.description}</span>
                    </span>
                  ) : (
                    <>
                      <span className="truncate font-medium">{item.label}</span>
                      {dot}
                    </>
                  )}
                </span>
                {item.badge && (
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                      item.badgeColor ?? "bg-gray-100 text-fg-muted dark:bg-white/10"
                    }`}
                  >
                    {item.badge}
                  </span>
                )}
              </>
            );
            const activate = (e: ReactMouseEvent) => {
              e.stopPropagation();
              if (item.disabled) {
                e.preventDefault();
                return;
              }
              // Back to the trigger, like a native menu. Done before the action so a dialog opened by the action
              // records the trigger as the element to return focus to.
              if (menuRef.current?.contains(document.activeElement)) triggerRef.current?.focus({ preventScroll: true });
              setOpen(false);
              item.onClick?.();
            };
            const setRef = (node: HTMLElement | null) => {
              itemRefs.current[myIndex] = node;
            };
            return item.href ? (
              <Link
                key={idx}
                ref={setRef}
                href={item.href}
                role="menuitem"
                tabIndex={-1}
                aria-disabled={item.disabled || undefined}
                className={cls}
                {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                onClick={activate}
              >
                {inner}
              </Link>
            ) : (
              <button
                key={idx}
                ref={setRef}
                type="button"
                role="menuitem"
                tabIndex={-1}
                aria-disabled={item.disabled || undefined}
                className={cls}
                onClick={activate}
              >
                {inner}
              </button>
            );
          })}
        </motion.div>
      )}
    </AnimatePresence>
  );

  // The wrapper keeps the old layout: a block in normal flow, content-sized inside a flex row.
  return (
    <>
      <div>{triggerNode}</div>
      {typeof window !== "undefined" && ReactDOM.createPortal(menu, document.body)}
    </>
  );
};

export default DropdownMenu;
